import { ActionsHook } from "@/lib/Components/Actions/ActionGroupButtons";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { BookingCancelDialog } from "@/app/Bookings/Components/Dialogs/BookingCancelDialog";
import { BookingStatus, FeatureFlagType } from "@/gql/graphql";
import { Booking } from "@/gql/schema";
import { PreviewPdfDialog } from "@/lib/Components/Dialog/PreviewPdfDialog";
import {
  ArrowTopRightOnSquareIcon,
  CheckIcon,
} from "@heroicons/react/20/solid";
import {
  CircleDotDashedIcon,
  CrownIcon,
  MailIcon,
  NotebookPenIcon,
  UserPenIcon,
} from "lucide-react";
import { CheckBadgeIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { PDFIcon } from "@/assets/Icons";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { toast } from "sonner";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import {
  bookingAcceptedMutation,
  mailBookingToCustomerMutation,
  mailBookingToSupplierMutation,
} from "@/app/Bookings/GraphQL/bookingMutations";
import { bookingPdfUrl } from "@/app/Bookings/Utils/bookingPdfUrl";
import { BookingConfirmDialog } from "@/app/Bookings/Components/Dialogs/BookingConfirmDialog";
import { BookingVipDialog } from "@/app/Bookings/Components/Dialogs/BookingVipDialog";
import { BookingPendingDialog } from "@/app/Bookings/Components/Dialogs/BookingPendingDialog";
import { useFeatureFlag } from "@/app/Meta/utils/useFeatureFlag";
import { BookingEditCustomerDetailsDialog } from "@/app/Bookings/Components/Dialogs/BookingEditCustomerDetailsDialog";
import { BookingEditReservationDetailsDialog } from "@/app/Bookings/Components/Dialogs/BookingEditReservationDetailsDialog";
import { BookingMailToDialog } from "@/app/Bookings/Components/Dialogs/BookingMailToDialog";

export function useBookingActions(): ActionsHook<
  Pick<Booking, "id" | "uuid" | "status" | "customer_url">
> {
  const isBookingAcceptedEnabled = useFeatureFlag(
    FeatureFlagType.BookingAcceptedState,
  );
  const { open: openCancelDialog } = useDialog(BookingCancelDialog);
  const { open: openConfirmDialog } = useDialog(BookingConfirmDialog);
  const { open: openVipDialog } = useDialog(BookingVipDialog);
  const { open: openPendingDialog } = useDialog(BookingPendingDialog);
  const { open: openMailToDialog } = useDialog(BookingMailToDialog);

  const { open: openPdf } = useDialog(PreviewPdfDialog);
  const { mutateAsync: mailToCustomer } = useGqlMutation(
    mailBookingToCustomerMutation,
  );
  const { mutateAsync: mailToSupplier } = useGqlMutation(
    mailBookingToSupplierMutation,
  );

  const { mutateAsync: acceptBooking } = useGqlMutation(
    bookingAcceptedMutation,
  );
  const { open: openCustomerDetailsDialog } = useDialog(
    BookingEditCustomerDetailsDialog,
  );
  const { open: openReservationDetailsDialog } = useDialog(
    BookingEditReservationDetailsDialog,
  );

  const { isAdmin } = useTenant();

  return (booking) => {
    return [
      {
        label: "Actions",
        items: [
          {
            actions: [
              {
                label: "Confirm",
                Icon: CheckIcon,
                color: "green",
                isHidden: [
                  BookingStatus.Confirmed,
                  BookingStatus.Completed,
                ].includes(booking.status),
                handleAction: () => {
                  openConfirmDialog({ bookingId: booking.id });
                },
              },
              {
                label: "Accept",
                Icon: CheckBadgeIcon,
                isHidden:
                  !isBookingAcceptedEnabled ||
                  ![
                    BookingStatus.AwaitingConfirmation,
                    BookingStatus.Vip,
                  ].includes(booking.status),
                handleAction: async () => {
                  await acceptBooking({
                    id: booking.id,
                  });

                  toast(
                    "Booking accepted! An email has been sent to the res team",
                  );
                },
                color: "indigo",
              },
              {
                label: "Pending",
                Icon: CircleDotDashedIcon,
                isHidden:
                  !isAdmin ||
                  [
                    BookingStatus.AwaitingConfirmation,
                    BookingStatus.Confirmed,
                    BookingStatus.Completed,
                    BookingStatus.Accepted,
                  ].includes(booking.status),
                handleAction: () => {
                  openPendingDialog({
                    bookingId: booking.id,
                  });
                },
                color: "yellow",
              },
              {
                label: "VIP",
                Icon: CrownIcon,
                color: "indigo",
                isAdminAction: true,
                isHidden: [BookingStatus.Vip].includes(booking.status),
                handleAction: () => {
                  openVipDialog({
                    bookingId: booking.id,
                  });
                },
              },
              {
                label: "Cancel",
                Icon: XMarkIcon,
                color: "red",
                isHidden: [
                  BookingStatus.AdminCancelled,
                  BookingStatus.SupplierCancelled,
                  BookingStatus.CustomerCancelled,
                  BookingStatus.AwaitingPayment,
                ].includes(booking.status),
                handleAction: () => {
                  openCancelDialog({ bookingId: booking.id });
                },
              },
              {
                label: "Change cancel status",
                Icon: XMarkIcon,
                color: "red",
                isHiddenTopBar: true,
                isHidden: ![
                  BookingStatus.AdminCancelled,
                  BookingStatus.SupplierCancelled,
                  BookingStatus.CustomerCancelled,
                ].includes(booking.status),
                handleAction: () => {
                  openCancelDialog({ bookingId: booking.id });
                },
              },
            ],
          },
          {
            actions: [
              {
                label: "Edit Customer Details",
                Icon: UserPenIcon,
                isHiddenTopBar: true,
                handleAction: () => {
                  openCustomerDetailsDialog({
                    bookingId: booking.id,
                  });
                },
              },
              {
                label: "Edit Reservation Details",
                Icon: NotebookPenIcon,
                isHiddenTopBar: true,
                handleAction: () => {
                  openReservationDetailsDialog({
                    bookingId: booking.id,
                  });
                },
              },
            ],
          },
          {
            actions: [
              {
                Icon: MailIcon,
                label: "Email customer",
                isAdminAction: true,
                handleAction: async () => {
                  await mailToCustomer({
                    id: booking.id,
                  });

                  toast("Email sent", {
                    description: "The email has been sent to the customer",
                  });
                },
              },
              {
                Icon: MailIcon,
                label: "Email supplier",
                isAdminAction: true,
                handleAction: async () => {
                  await mailToSupplier({
                    id: booking.id,
                  });

                  toast("Email sent", {
                    description: "The email has been sent to the supplier",
                  });
                },
              },
              {
                Icon: MailIcon,
                label: "Email to...",
                handleAction: async () => {
                  openMailToDialog({
                    bookingId: booking.id,
                  });
                },
              },
            ],
          },
          {
            actions: [
              {
                label: "Pdf",
                Icon: PDFIcon,
                handleAction: async () => {
                  openPdf({
                    url: bookingPdfUrl(booking.uuid),
                  });
                },
              },
            ],
          },
          {
            actions: [
              {
                label: "View",
                Icon: ArrowTopRightOnSquareIcon,
                handleAction: async () => {
                  window.open(booking.customer_url, "_blank");
                },
              },
            ],
          },
        ],
      },
    ];
  };
}
